/* eslint-disable indent, no-nested-ternary, no-use-before-define, no-unused-expressions */
import React, {
	createContext,
	FC,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import {
	getStoredAccessToken,
	shouldMountAuthenticatedGlobalHosts,
} from '../../helpers/authSession';
import {
	approveAutopilotAction,
	commentOnAutopilotAction,
	createAutopilotCancelSource,
	createAutopilotConversation,
	dismissAutopilotAction,
	getAutopilotBootstrap,
	getAutopilotUpdates,
	markAutopilotActionSeen,
	retryAutopilotAction,
	sendAutopilotConversationMessage,
	undoAutopilotAction,
	updateOrganizationSelfDriving,
	updateProjectAutomationMode,
} from './api';
import {
	AutopilotAction,
	AutopilotAffectedResource,
	AutopilotActionPreview,
	AutopilotAuthorizedOrganization,
	AutopilotBootstrapPage,
	AutopilotMessage,
	AutopilotUpdatePage,
	AutomationMode,
	OrganizationAutomationSettings,
	ProjectAutomationSettings,
	SelfDrivingAcknowledgement,
	isPendingApproval,
	requiresAutopilotAttention,
} from './types';
import { emitAutopilotProjectDataChanged } from './events';
import {
	acknowledgeActivityNotifications,
	getAllActivityNotifications,
} from '../activityNotifications/api';
import { activityNotificationPath } from '../activityNotifications/navigation';
import { ActivityAcknowledgeReason, ActivityNotification } from '../activityNotifications/types';

const POLL_INTERVAL_MS = 15000;
const EXECUTING_POLL_INTERVAL_MS = 5000;
const MAX_POLL_BACKOFF_MS = 120000;
const DEFAULT_RAIL_WIDTH = 420;
export const MIN_AUTOPILOT_RAIL_WIDTH = 360;
export const MAX_AUTOPILOT_RAIL_WIDTH = 720;
export const SELF_DRIVING_POLICY_VERSION = '2026-09-10';

type ActionCommand = 'approve' | 'dismiss' | 'retry' | 'undo';

export interface AutopilotContextValue {
	enabled: boolean;
	authenticated: boolean;
	isOpen: boolean;
	openRail: (requestFocus?: boolean) => void;
	closeRail: () => void;
	toggleRail: () => void;
	focusRequest: number;
	railWidth: number;
	setRailWidth: (width: number) => void;
	resetRailWidth: () => void;
	loading: boolean;
	refreshing: boolean;
	offline: boolean;
	error: string;
	lastUpdatedAt: number | null;
	refresh: () => Promise<void>;
	actions: AutopilotAction[];
	messages: AutopilotMessage[];
	activityNotifications: ActivityNotification[];
	activityBusy: Record<string, boolean>;
	activityError: string;
	viewActivityNotification: (notification: ActivityNotification) => void;
	dismissActivityNotification: (notification: ActivityNotification) => Promise<void>;
	dismissAllActivityNotifications: (notifications: ActivityNotification[]) => Promise<void>;
	pendingCount: number;
	actionableCount: number;
	actionBusy: Record<string, boolean>;
	actionErrors: Record<string, string>;
	runActionCommand: (
		action: AutopilotAction,
		command: ActionCommand,
		editedPreview?: AutopilotActionPreview,
	) => Promise<boolean>;
	commentOnAction: (action: AutopilotAction, comment: string) => Promise<boolean>;
	currentProjectId: number | null;
	currentProjectSettings: ProjectAutomationSettings | null;
	organizationSettings: OrganizationAutomationSettings | null;
	selfDrivingReleaseEnabled: boolean;
	authorizedOrganizations: AutopilotAuthorizedOrganization[];
	selectedOrganizationId: number | null;
	setSelectedOrganizationId: (organizationId: number | null) => void;
	modeBusy: boolean;
	modeError: string;
	requestModeChange: (mode: AutomationMode) => Promise<void>;
	selfDrivingDialogOpen: boolean;
	confirmSelfDriving: () => Promise<void>;
	cancelSelfDriving: () => void;
	organizationBusy: boolean;
	setOrganizationSelfDrivingAllowed: (allowed: boolean) => Promise<void>;
	draft: string;
	setDraft: (value: string) => void;
	questionScope: 'PROJECT' | 'ORGANIZATION';
	setQuestionScope: (scope: 'PROJECT' | 'ORGANIZATION') => void;
	questionBusy: boolean;
	sendQuestion: (overrideContent?: string) => Promise<void>;
	stopQuestion: () => void;
	startNewChat: () => Promise<void>;
	markActionSeen: (action: AutopilotAction) => Promise<void>;
	registerCardDraft: (actionId: number | string, dirty: boolean) => void;
	hasUnsavedCardDrafts: boolean;
	focusActionId: string | null;
	consumeActionFocus: (actionId: number | string) => void;
	liveAnnouncement: string;
	transientPreviews: AutopilotTransientPreview[];
	dismissTransientPreview: (previewId: string) => void;
	openTransientPreview: (preview: AutopilotTransientPreview) => void;
	focusActivityId: string | null;
	consumeActivityFocus: (recipientId: number | string) => void;
}

export type AutopilotTransientPreview =
	| {
			id: string;
			kind: 'ACTION';
			occurredAt: string;
			action: AutopilotAction;
	  }
	| {
			id: string;
			kind: 'ACTIVITY';
			occurredAt: string;
			notification: ActivityNotification;
	  };

export const AUTOPILOT_DEFAULT_CONTEXT: AutopilotContextValue = {
	enabled: false,
	authenticated: false,
	isOpen: false,
	openRail: () => undefined,
	closeRail: () => undefined,
	toggleRail: () => undefined,
	focusRequest: 0,
	railWidth: DEFAULT_RAIL_WIDTH,
	setRailWidth: () => undefined,
	resetRailWidth: () => undefined,
	loading: false,
	refreshing: false,
	offline: false,
	error: '',
	lastUpdatedAt: null,
	refresh: async () => undefined,
	actions: [],
	messages: [],
	activityNotifications: [],
	activityBusy: {},
	activityError: '',
	viewActivityNotification: () => undefined,
	dismissActivityNotification: async () => undefined,
	dismissAllActivityNotifications: async () => undefined,
	pendingCount: 0,
	actionableCount: 0,
	actionBusy: {},
	actionErrors: {},
	runActionCommand: async () => false,
	commentOnAction: async () => false,
	currentProjectId: null,
	currentProjectSettings: null,
	organizationSettings: null,
	selfDrivingReleaseEnabled: false,
	authorizedOrganizations: [],
	selectedOrganizationId: null,
	setSelectedOrganizationId: () => undefined,
	modeBusy: false,
	modeError: '',
	requestModeChange: async () => undefined,
	selfDrivingDialogOpen: false,
	confirmSelfDriving: async () => undefined,
	cancelSelfDriving: () => undefined,
	organizationBusy: false,
	setOrganizationSelfDrivingAllowed: async () => undefined,
	draft: '',
	setDraft: () => undefined,
	questionScope: 'PROJECT',
	setQuestionScope: () => undefined,
	questionBusy: false,
	sendQuestion: async () => undefined,
	stopQuestion: () => undefined,
	startNewChat: async () => undefined,
	markActionSeen: async () => undefined,
	registerCardDraft: () => undefined,
	hasUnsavedCardDrafts: false,
	focusActionId: null,
	consumeActionFocus: () => undefined,
	liveAnnouncement: '',
	transientPreviews: [],
	dismissTransientPreview: () => undefined,
	openTransientPreview: () => undefined,
	focusActivityId: null,
	consumeActivityFocus: () => undefined,
};

export const AutopilotContext = createContext<AutopilotContextValue>(AUTOPILOT_DEFAULT_CONTEXT);

export const useAutopilot = (): AutopilotContextValue => useContext(AutopilotContext);

const clampWidth = (value: number) =>
	Math.max(MIN_AUTOPILOT_RAIL_WIDTH, Math.min(MAX_AUTOPILOT_RAIL_WIDTH, Math.round(value)));

export const currentProjectIdFromPath = (pathname: string): number | null => {
	const match = String(pathname || '').match(
		/^\/(?:project|multi-package-interactive-review|exclusivity-review)\/(\d+)(?:\/|$)/,
	);
	if (!match) return null;
	const projectId = Number(match[1]);
	return Number.isInteger(projectId) && projectId > 0 ? projectId : null;
};

const actionKey = (id: number | string) => String(id);
const messageKey = (id: number | string) => String(id);
const ACTION_URGENCY_RANK: Record<string, number> = {
	CRITICAL: 0,
	HIGH: 1,
	MEDIUM: 2,
	LOW: 3,
};

const actionUpdatedAt = (action: AutopilotAction) =>
	Date.parse(action.updatedAt || action.decidedAt || action.createdAt || '') || 0;

const isActionAtLeastAsFresh = (candidate: AutopilotAction, current?: AutopilotAction) => {
	if (!current) return true;
	const candidateVersion = Number(candidate.version || 0);
	const currentVersion = Number(current.version || 0);
	if (candidateVersion !== currentVersion) return candidateVersion > currentVersion;
	return actionUpdatedAt(candidate) >= actionUpdatedAt(current);
};

export const mergeActions = (
	current: AutopilotAction[],
	incoming: AutopilotAction[],
): AutopilotAction[] => {
	const merged = new Map<string, AutopilotAction>();
	current.forEach((action) => {
		const key = actionKey(action.id);
		if (isActionAtLeastAsFresh(action, merged.get(key))) merged.set(key, action);
	});
	incoming.forEach((action) => {
		const key = actionKey(action.id);
		const previous = merged.get(key);
		if (!isActionAtLeastAsFresh(action, previous)) return;
		if (action.status === 'STALE' && action.acknowledgementAvailable !== true) {
			merged.delete(key);
			return;
		}
		merged.set(key, { ...previous, ...action });
	});
	const canonical = new Map<string, AutopilotAction>();
	Array.from(merged.values()).forEach((action) => {
		const key =
			action.type === 'A8_ADD_MISSING_QUOTE_ALTERNATES' && action.canonicalKey
				? `A8:${action.canonicalKey}`
				: `ACTION:${actionKey(action.id)}`;
		const previous = canonical.get(key);
		if (!previous) {
			canonical.set(key, action);
			return;
		}
		if (previous.status === 'RESOLVED_ELSEWHERE' && action.status !== 'RESOLVED_ELSEWHERE') {
			canonical.set(key, action);
			return;
		}
		if (action.status === 'RESOLVED_ELSEWHERE' && previous.status !== 'RESOLVED_ELSEWHERE') return;
		const previousId = Number(previous.id);
		const actionId = Number(action.id);
		if (
			(Number.isFinite(actionId) && Number.isFinite(previousId) && actionId < previousId) ||
			(!Number.isFinite(actionId) && isActionAtLeastAsFresh(action, previous))
		) {
			canonical.set(key, action);
		}
	});
	return Array.from(canonical.values()).sort((left, right) => {
		const leftUrgency =
			ACTION_URGENCY_RANK[String(left.urgency || left.risk || 'MEDIUM').toUpperCase()] ?? 2;
		const rightUrgency =
			ACTION_URGENCY_RANK[String(right.urgency || right.risk || 'MEDIUM').toUpperCase()] ?? 2;
		if (leftUrgency !== rightUrgency) return leftUrgency - rightUrgency;
		const leftDeadline = left.deadlineAt
			? new Date(left.deadlineAt).getTime()
			: Number.MAX_SAFE_INTEGER;
		const rightDeadline = right.deadlineAt
			? new Date(right.deadlineAt).getTime()
			: Number.MAX_SAFE_INTEGER;
		if (leftDeadline !== rightDeadline) return leftDeadline - rightDeadline;
		return new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
	});
};

const mergeMessages = (current: AutopilotMessage[], incoming: AutopilotMessage[]) => {
	const merged = new Map<string, AutopilotMessage>();
	current.forEach((message) => merged.set(messageKey(message.id), message));
	incoming.forEach((message) => merged.set(messageKey(message.id), message));
	return Array.from(merged.values()).sort(
		(left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
	);
};

const mergeProjectSettings = (
	current: ProjectAutomationSettings[],
	incoming: ProjectAutomationSettings[],
) => {
	const merged = new Map<number, ProjectAutomationSettings>();
	current.forEach((settings) => merged.set(settings.projectId, settings));
	incoming.forEach((settings) => merged.set(settings.projectId, settings));
	return Array.from(merged.values());
};

const mergeOrganizationSettings = (
	current: OrganizationAutomationSettings[],
	incoming: OrganizationAutomationSettings[],
) => {
	const merged = new Map<number, OrganizationAutomationSettings>();
	current.forEach((settings) => merged.set(settings.organizationId, settings));
	incoming.forEach((settings) => merged.set(settings.organizationId, settings));
	return Array.from(merged.values());
};

const activityKey = (notification: ActivityNotification) => String(notification.recipientId);

const storageAvailable = (storage: Storage | undefined): storage is Storage => Boolean(storage);

const transientPreviewStorageKey = (userId: string) => `autopilot:transient-preview-seen:${userId}`;

const readSessionPreviewIds = (userId: string) => {
	if (!storageAvailable(typeof window !== 'undefined' ? window.sessionStorage : undefined)) {
		return new Set<string>();
	}
	try {
		const stored = JSON.parse(
			window.sessionStorage.getItem(transientPreviewStorageKey(userId)) || '[]',
		);
		return new Set<string>(
			(Array.isArray(stored) ? stored : [])
				.map((value) => String(value || '').trim())
				.filter(Boolean),
		);
	} catch (_error) {
		return new Set<string>();
	}
};

const writeSessionPreviewIds = (userId: string, values: Set<string>) => {
	if (!storageAvailable(typeof window !== 'undefined' ? window.sessionStorage : undefined)) {
		return;
	}
	try {
		window.sessionStorage.setItem(
			transientPreviewStorageKey(userId),
			JSON.stringify(Array.from(values)),
		);
	} catch (_error) {
		// The in-memory set remains authoritative when storage is unavailable or full.
	}
};

const transientPreviewTime = (preview: AutopilotTransientPreview) => {
	const value = new Date(preview.occurredAt).getTime();
	return Number.isFinite(value) ? value : 0;
};

export const sortAutopilotTransientPreviews = (previews: AutopilotTransientPreview[]) =>
	previews.slice().sort((left, right) => {
		const timeDelta = transientPreviewTime(left) - transientPreviewTime(right);
		if (timeDelta !== 0) return timeDelta;
		if (left.kind !== right.kind) return left.kind === 'ACTION' ? -1 : 1;
		return left.id.localeCompare(right.id);
	});

const actionExecutionRecord = (action: AutopilotAction): Record<string, unknown> =>
	action.executionResult && typeof action.executionResult === 'object'
		? (action.executionResult as Record<string, unknown>)
		: {};

const actionAffectedResources = (
	action: AutopilotAction,
): Array<AutopilotAffectedResource | string> => {
	const result = actionExecutionRecord(action);
	const resultResources = Array.isArray(result.changedResources)
		? result.changedResources
		: Array.isArray(result.affectedResources)
		? result.affectedResources
		: [];
	const explicit = Array.isArray(action.affectedResources) ? action.affectedResources : [];
	const resources = (explicit.length ? explicit : resultResources).filter(
		(resource): resource is AutopilotAffectedResource | string =>
			typeof resource === 'string' || (typeof resource === 'object' && resource !== null),
	);
	if (resources.length) return resources;
	return action.type === 'A1_APPLY_PACKAGE_CHANGES'
		? ['CHANGE_LOG', 'CONTRACT_INCLUSION', 'SCOPE_CHECKLIST', 'BID_LEVELING_CHECKLIST']
		: [];
};

const errorBody = (error: any) =>
	error?.response?.data !== undefined
		? error.response.data
		: error?.data !== undefined
		? error.data
		: error;
const errorText = (error: any, fallback: string) => {
	const body = errorBody(error);
	return String(body?.message || body?.error || fallback);
};
export const isVersionConflict = (error: any) => {
	const body = errorBody(error);
	const code = body?.code || body?.details?.code || error?.code;
	return (
		Number(error?.status || error?.response?.status || 0) === 409 &&
		String(code || '').toUpperCase() === 'VERSION_CONFLICT'
	);
};
export const autopilotActionErrorMessage = (error: any) =>
	isVersionConflict(error)
		? 'This action changed elsewhere. It has been refreshed.'
		: errorText(error, 'The action could not be completed. Try again.');

interface Props {
	enabled: boolean;
}

const AutopilotProvider: FC<Props> = ({ children, enabled }) => {
	const history = useHistory();
	const location = useLocation();
	const userRole = localStorage.getItem('USER_ROLE');
	const userId = localStorage.getItem('USER_ID') || 'unknown';
	const supportedRole = userRole === 'OA' || userRole === 'A' || userRole === 'U';
	const authenticated =
		enabled &&
		supportedRole &&
		shouldMountAuthenticatedGlobalHosts(location.pathname, getStoredAccessToken());
	const currentProjectId = currentProjectIdFromPath(location.pathname);
	const [isOpen, setIsOpen] = useState(false);
	const isOpenRef = useRef(false);
	const [focusRequest, setFocusRequest] = useState(0);
	const [railWidth, setRailWidthState] = useState(DEFAULT_RAIL_WIDTH);
	const [loading, setLoading] = useState(false);
	const [refreshing, setRefreshing] = useState(false);
	const [offline, setOffline] = useState(
		typeof navigator !== 'undefined' ? !navigator.onLine : false,
	);
	const [error, setError] = useState('');
	const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
	const [actions, setActions] = useState<AutopilotAction[]>([]);
	const [messages, setMessages] = useState<AutopilotMessage[]>([]);
	const [activityNotifications, setActivityNotifications] = useState<ActivityNotification[]>([]);
	const [activityBusy, setActivityBusy] = useState<Record<string, boolean>>({});
	const [activityError, setActivityError] = useState('');
	const [projectSettings, setProjectSettings] = useState<ProjectAutomationSettings[]>([]);
	const [organizationSettingsList, setOrganizationSettingsList] = useState<
		OrganizationAutomationSettings[]
	>([]);
	const [authorizedOrganizations, setAuthorizedOrganizations] = useState<
		AutopilotAuthorizedOrganization[]
	>([]);
	const [selfDrivingReleaseEnabled, setSelfDrivingReleaseEnabled] = useState(false);
	const [selectedOrganizationId, setSelectedOrganizationIdState] = useState<number | null>(null);
	const [conversationId, setConversationId] = useState<number | string | null>(null);
	const [cursor, setCursor] = useState<string | null>(null);
	const [actionBusy, setActionBusy] = useState<Record<string, boolean>>({});
	const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
	const [modeBusy, setModeBusy] = useState(false);
	const [modeError, setModeError] = useState('');
	const [selfDrivingDialogOpen, setSelfDrivingDialogOpen] = useState(false);
	const [organizationBusy, setOrganizationBusy] = useState(false);
	const [questionScope, setQuestionScope] = useState<'PROJECT' | 'ORGANIZATION'>(
		currentProjectId ? 'PROJECT' : 'ORGANIZATION',
	);
	const [questionBusy, setQuestionBusy] = useState(false);
	const [cardDraftIds, setCardDraftIds] = useState<string[]>([]);
	const [focusActionId, setFocusActionId] = useState<string | null>(null);
	const [focusActivityId, setFocusActivityId] = useState<string | null>(null);
	const [transientPreviews, setTransientPreviews] = useState<AutopilotTransientPreview[]>([]);
	const [drafts, setDrafts] = useState<Record<string, string>>({});
	const [liveAnnouncement, setLiveAnnouncement] = useState('');
	const [conversationContextRevision, setConversationContextRevision] = useState(0);
	const inFlightRef = useRef<Promise<void> | null>(null);
	const requestSequenceRef = useRef(0);
	const activeRequestIdRef = useRef(0);
	const cancelSourceRef = useRef<ReturnType<typeof createAutopilotCancelSource> | null>(null);
	const questionCancelSourceRef = useRef<ReturnType<typeof createAutopilotCancelSource> | null>(
		null,
	);
	const activeQuestionMessageIdRef = useRef<string | null>(null);
	const seenActionIdsRef = useRef<Set<string>>(new Set());
	const knownActionVersionsRef = useRef<Map<string, number>>(new Map());
	const knownActionStatusesRef = useRef<Map<string, string>>(new Map());
	const previewSeenIdsRef = useRef<Set<string>>(readSessionPreviewIds(userId));
	const emittedResourceRefreshesRef = useRef<Set<string>>(new Set());
	const initialFetchCompleteRef = useRef(false);
	const sessionActivityBootstrappedRef = useRef(false);
	const autoOpenedRef = useRef(false);
	const mountedRef = useRef(true);
	const consecutiveFailuresRef = useRef(0);
	const conversationContextRef = useRef('');
	const conversationHistoryRef = useRef<
		Map<string, { conversationId: number | string | null; messages: AutopilotMessage[] }>
	>(new Map());
	const currentProjectSettings = useMemo(
		() => projectSettings.find((settings) => settings.projectId === currentProjectId) || null,
		[currentProjectId, projectSettings],
	);
	const organizationSettings = useMemo(() => {
		const projectOrganizationId = currentProjectSettings?.organizationId;
		if (projectOrganizationId) {
			return (
				organizationSettingsList.find(
					(settings) => settings.organizationId === projectOrganizationId,
				) || null
			);
		}
		if (selectedOrganizationId) {
			return (
				organizationSettingsList.find(
					(settings) => settings.organizationId === selectedOrganizationId,
				) || null
			);
		}
		return organizationSettingsList.length === 1 ? organizationSettingsList[0] : null;
	}, [currentProjectSettings, organizationSettingsList, selectedOrganizationId]);
	const conversationOrganizationId =
		currentProjectSettings?.organizationId || selectedOrganizationId || null;
	const conversationContextKey =
		questionScope === 'PROJECT'
			? `PROJECT:${currentProjectId || 'none'}`
			: `ORGANIZATION:${conversationOrganizationId || 'unselected'}`;

	const organizationId = organizationSettings?.organizationId || 'unknown';
	const widthStorageKey = `autopilot:rail-width:${userId}:${organizationId}`;
	const draftContextKey =
		questionScope === 'PROJECT' && currentProjectId
			? String(currentProjectId)
			: `organization-${conversationOrganizationId || 'unselected'}`;
	const draftStorageKey = `autopilot:draft:${userId}:${draftContextKey}`;
	const draft =
		drafts[draftStorageKey] !== undefined
			? drafts[draftStorageKey]
			: storageAvailable(typeof window !== 'undefined' ? window.sessionStorage : undefined)
			? window.sessionStorage.getItem(draftStorageKey) || ''
			: '';

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			cancelSourceRef.current?.cancel('Autopilot host unmounted');
			questionCancelSourceRef.current?.cancel('Autopilot host unmounted');
		};
	}, []);

	useEffect(() => {
		if (!authenticated) {
			cancelSourceRef.current?.cancel('Authentication ended');
			questionCancelSourceRef.current?.cancel('Authentication ended');
			inFlightRef.current = null;
			initialFetchCompleteRef.current = false;
			autoOpenedRef.current = false;
			setActions([]);
			setMessages([]);
			setActivityNotifications([]);
			setActivityBusy({});
			setActivityError('');
			conversationHistoryRef.current.clear();
			setProjectSettings([]);
			setOrganizationSettingsList([]);
			setSelfDrivingReleaseEnabled(false);
			setAuthorizedOrganizations([]);
			setSelectedOrganizationIdState(null);
			setConversationId(null);
			setQuestionBusy(false);
			setCardDraftIds([]);
			setFocusActionId(null);
			setFocusActivityId(null);
			setTransientPreviews([]);
			seenActionIdsRef.current.clear();
			knownActionVersionsRef.current.clear();
			knownActionStatusesRef.current.clear();
			sessionActivityBootstrappedRef.current = false;
			previewSeenIdsRef.current = readSessionPreviewIds(userId);
			emittedResourceRefreshesRef.current.clear();
			setCursor(null);
			setIsOpen(false);
		}
	}, [authenticated, userId]);

	useEffect(() => {
		if (!currentProjectId && questionScope === 'PROJECT') setQuestionScope('ORGANIZATION');
	}, [currentProjectId, questionScope]);

	useEffect(() => {
		const nextContext = conversationContextKey;
		if (conversationContextRef.current && conversationContextRef.current !== nextContext) {
			conversationHistoryRef.current.set(conversationContextRef.current, {
				conversationId,
				messages,
			});
			const previousConversation = conversationHistoryRef.current.get(nextContext);
			cancelSourceRef.current?.cancel('Conversation context changed');
			questionCancelSourceRef.current?.cancel('Conversation context changed');
			questionCancelSourceRef.current = null;
			activeQuestionMessageIdRef.current = null;
			inFlightRef.current = null;
			initialFetchCompleteRef.current = false;
			setConversationId(previousConversation?.conversationId || null);
			setActions([]);
			setMessages(previousConversation?.messages || []);
			setActivityNotifications([]);
			setActivityError('');
			setCursor(null);
			setQuestionBusy(false);
			setConversationContextRevision((current) => current + 1);
		}
		conversationContextRef.current = nextContext;
	}, [conversationContextKey, conversationId, messages]);

	useEffect(() => {
		if (!authenticated || typeof window === 'undefined') return;
		const stored = Number(window.localStorage.getItem(widthStorageKey));
		if (Number.isFinite(stored)) setRailWidthState(clampWidth(stored));
	}, [authenticated, widthStorageKey]);

	const emitAffectedResourceRefresh = useCallback((action: AutopilotAction) => {
		const resources = actionAffectedResources(action);
		if (!resources.length || !action.projectId) return;
		const refreshKey = `${actionKey(action.id)}:${action.version}:${action.status}`;
		if (emittedResourceRefreshesRef.current.has(refreshKey)) return;
		emittedResourceRefreshesRef.current.add(refreshKey);
		const result = actionExecutionRecord(action);
		emitAutopilotProjectDataChanged({
			actionId: action.id,
			actionType: action.type,
			bidPackageId: action.bidPackageId,
			changeLogItemId: Number(result.changeLogItemId) || null,
			executionStatus: action.status,
			projectId: action.projectId,
			resources,
		});
	}, []);

	const rememberPreviewIds = useCallback(
		(ids: string[]) => {
			ids.forEach((id) => previewSeenIdsRef.current.add(id));
			writeSessionPreviewIds(userId, previewSeenIdsRef.current);
		},
		[userId],
	);

	const enqueueTransientPreviews = useCallback(
		(candidates: AutopilotTransientPreview[]) => {
			const candidateIds = new Set<string>();
			const next = candidates.filter((candidate) => {
				if (previewSeenIdsRef.current.has(candidate.id) || candidateIds.has(candidate.id))
					return false;
				candidateIds.add(candidate.id);
				return true;
			});
			if (!next.length) return;
			setTransientPreviews((current) => {
				const merged = new Map(current.map((preview) => [preview.id, preview]));
				next.forEach((preview) => merged.set(preview.id, preview));
				return sortAutopilotTransientPreviews(Array.from(merged.values()));
			});
			candidateIds.forEach((id) => previewSeenIdsRef.current.add(id));
			writeSessionPreviewIds(userId, previewSeenIdsRef.current);
		},
		[userId],
	);

	const applyActivityPage = useCallback(
		(incoming: ActivityNotification[]) => {
			const next = Array.isArray(incoming) ? incoming : [];
			const previews = next.map<AutopilotTransientPreview>((notification) => ({
				id: `ACTIVITY:${activityKey(notification)}`,
				kind: 'ACTIVITY',
				notification,
				occurredAt: notification.occurredAt,
			}));
			if (!sessionActivityBootstrappedRef.current) {
				rememberPreviewIds(previews.map((preview) => preview.id));
				sessionActivityBootstrappedRef.current = true;
			} else {
				const newActivities = previews.filter(
					(preview) => !previewSeenIdsRef.current.has(preview.id),
				);
				rememberPreviewIds(previews.map((preview) => preview.id));
				if (newActivities.length) {
					setLiveAnnouncement(
						`${newActivities.length} new activity ${
							newActivities.length === 1 ? 'update is' : 'updates are'
						} available in Recent Actions.`,
					);
				}
			}
			setActivityNotifications(next);
		},
		[rememberPreviewIds],
	);

	const applyPage = useCallback(
		(page: AutopilotUpdatePage | AutopilotBootstrapPage, announceNewActions = false) => {
			const nextActions = Array.isArray(page.actions) ? page.actions : [];
			const nextMessages = Array.isArray(page.messages) ? page.messages : [];
			const nextSettings = Array.isArray(page.projectSettings) ? page.projectSettings : [];
			const freshActions = nextActions.filter(
				(action) =>
					!['COMPLETED', 'STALE'].includes(action.status) &&
					!knownActionVersionsRef.current.has(actionKey(action.id)),
			);
			const newlyCompletedActions = nextActions.filter(
				(action) =>
					action.status === 'COMPLETED' &&
					knownActionStatusesRef.current.get(actionKey(action.id)) !== 'COMPLETED',
			);
			const actionPreviews = freshActions.map<AutopilotTransientPreview>((action) => ({
				action,
				id: `ACTION:${actionKey(action.id)}`,
				kind: 'ACTION',
				occurredAt: action.createdAt,
			}));
			if (announceNewActions) {
				enqueueTransientPreviews(actionPreviews);
			} else {
				rememberPreviewIds(nextActions.map((action) => `ACTION:${actionKey(action.id)}`));
			}
			nextActions.forEach((action) => {
				knownActionVersionsRef.current.set(actionKey(action.id), Number(action.version || 0));
				knownActionStatusesRef.current.set(actionKey(action.id), action.status);
			});
			setActions((current) => mergeActions(current, nextActions));
			setMessages((current) => mergeMessages(current, nextMessages));
			setProjectSettings((current) => mergeProjectSettings(current, nextSettings));
			if (page.organizationSettings) {
				const nextOrganizationSettings = Array.isArray(page.organizationSettings)
					? page.organizationSettings
					: [page.organizationSettings];
				setOrganizationSettingsList((current) =>
					mergeOrganizationSettings(current, nextOrganizationSettings),
				);
			}
			if (typeof page.selfDrivingReleaseEnabled === 'boolean') {
				setSelfDrivingReleaseEnabled(page.selfDrivingReleaseEnabled);
				if (!page.selfDrivingReleaseEnabled) setSelfDrivingDialogOpen(false);
			}
			if (Array.isArray(page.authorizedOrganizations)) {
				const allowed = page.authorizedOrganizations.filter((organization) =>
					Number.isInteger(Number(organization.organizationId)),
				);
				setAuthorizedOrganizations(allowed);
				const projectOrganizationId = nextSettings.find(
					(settings) => settings.projectId === currentProjectId,
				)?.organizationId;
				setSelectedOrganizationIdState((current) => {
					if (projectOrganizationId) return projectOrganizationId;
					if (current && allowed.some((item) => item.organizationId === current)) return current;
					return allowed[0]?.organizationId || null;
				});
			}
			if (page.conversationId !== undefined && page.conversationId !== null) {
				setConversationId(page.conversationId);
			}
			if (page.updatesCursor) setCursor(page.updatesCursor);
			if (announceNewActions && (freshActions.length || newlyCompletedActions.length)) {
				const announcements: string[] = [];
				if (freshActions.length) {
					announcements.push(
						`${freshActions.length} new ${
							freshActions.length === 1 ? 'Autopilot Chat card is' : 'Autopilot Chat cards are'
						} available.`,
					);
				}
				if (newlyCompletedActions.length) {
					announcements.push(
						`${newlyCompletedActions.length} ${
							newlyCompletedActions.length === 1 ? 'action has' : 'actions have'
						} completed and moved to Recent actions.`,
					);
				}
				setLiveAnnouncement(announcements.join(' '));
			}
			if (announceNewActions) {
				nextActions
					.filter((action) => action.status === 'COMPLETED')
					.forEach(emitAffectedResourceRefresh);
			}
			setLastUpdatedAt(Date.now());
		},
		[currentProjectId, emitAffectedResourceRefresh, enqueueTransientPreviews, rememberPreviewIds],
	);

	const fetchData = useCallback(
		(initial = false): Promise<void> => {
			if (!authenticated) return Promise.resolve();
			if (inFlightRef.current) return inFlightRef.current;
			const source = createAutopilotCancelSource();
			const requestContextKey = conversationContextKey;
			cancelSourceRef.current = source;
			const requestId = requestSequenceRef.current + 1;
			requestSequenceRef.current = requestId;
			activeRequestIdRef.current = requestId;
			if (initial) setLoading(true);
			else setRefreshing(true);
			setOffline(typeof navigator !== 'undefined' ? !navigator.onLine : false);

			const request = (async () => {
				const activityRequest = getAllActivityNotifications(source.token)
					.then((notifications) => {
						if (!mountedRef.current || conversationContextRef.current !== requestContextKey) return;
						applyActivityPage(notifications);
						setActivityError('');
					})
					.catch((activityRequestError) => {
						if (axios.isCancel(activityRequestError) || !mountedRef.current) return;
						setActivityError('Activity updates could not refresh. Showing the last saved updates.');
					});
				try {
					if (initial) {
						let pageCursor: string | number | null = null;
						let pageCount = 0;
						let actionableFound = 0;
						do {
							const page: AutopilotBootstrapPage = await getAutopilotBootstrap(
								pageCursor,
								100,
								source.token,
								currentProjectId,
								conversationOrganizationId,
								questionScope,
							);
							if (!mountedRef.current || conversationContextRef.current !== requestContextKey)
								return;
							applyPage(page, false);
							actionableFound += (page.actions || []).filter((action) =>
								requiresAutopilotAttention(action),
							).length;
							pageCursor = page.nextCursor || null;
							pageCount += 1;
						} while (pageCursor !== null && pageCount < 100);
						initialFetchCompleteRef.current = true;
						if (actionableFound > 0 && !autoOpenedRef.current) {
							autoOpenedRef.current = true;
							setIsOpen(true);
							setLiveAnnouncement(
								`${actionableFound} ${
									actionableFound === 1 ? 'item needs' : 'items need'
								} your attention.`,
							);
						}
					} else {
						const page = await getAutopilotUpdates(
							cursor,
							source.token,
							currentProjectId,
							conversationOrganizationId,
							questionScope,
						);
						if (!mountedRef.current || conversationContextRef.current !== requestContextKey) return;
						applyPage(page, true);
					}
					setError('');
					setOffline(false);
					consecutiveFailuresRef.current = 0;
				} catch (requestError) {
					if (axios.isCancel(requestError)) return;
					if (!mountedRef.current) return;
					setOffline(typeof navigator !== 'undefined' ? !navigator.onLine : false);
					setError(
						errorText(
							requestError,
							initial
								? 'Autopilot Chat could not be loaded. Your work is still saved.'
								: 'Autopilot Chat could not refresh. Showing the last update.',
						),
					);
					consecutiveFailuresRef.current = Math.min(3, consecutiveFailuresRef.current + 1);
				} finally {
					await activityRequest;
					if (mountedRef.current) {
						setLoading(false);
						setRefreshing(false);
					}
					if (cancelSourceRef.current === source) cancelSourceRef.current = null;
					if (activeRequestIdRef.current === requestId) {
						activeRequestIdRef.current = 0;
						inFlightRef.current = null;
					}
				}
			})();
			inFlightRef.current = request;
			return request;
		},
		[
			applyActivityPage,
			applyPage,
			authenticated,
			conversationContextKey,
			conversationOrganizationId,
			currentProjectId,
			cursor,
			questionScope,
		],
	);

	const hasExecutingActions = useMemo(
		() => actions.some((action) => action.status === 'EXECUTING'),
		[actions],
	);

	useEffect(() => {
		isOpenRef.current = isOpen;
	}, [isOpen]);

	useEffect(() => {
		if (!authenticated || initialFetchCompleteRef.current) return;
		fetchData(true);
	}, [authenticated, conversationContextRevision, fetchData]);

	const setSelectedOrganizationId = useCallback(
		(organizationId: number | null) => {
			if (
				organizationId !== null &&
				!authorizedOrganizations.some((item) => item.organizationId === organizationId)
			) {
				return;
			}
			setSelectedOrganizationIdState(organizationId);
		},
		[authorizedOrganizations],
	);

	useEffect(() => {
		if (!authenticated) return undefined;
		let stopped = false;
		let timeoutId: number | null = null;
		const clearScheduledPoll = () => {
			if (timeoutId === null) return;
			window.clearTimeout(timeoutId);
			timeoutId = null;
		};
		const schedule = () => {
			if (stopped) return;
			clearScheduledPoll();
			const pollInterval = hasExecutingActions ? EXECUTING_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
			const base = Math.min(
				MAX_POLL_BACKOFF_MS,
				pollInterval * 2 ** consecutiveFailuresRef.current,
			);
			const jitter = Math.round(base * (Math.random() * 0.2 - 0.1));
			timeoutId = window.setTimeout(() => {
				timeoutId = null;
				poll();
			}, Math.max(pollInterval, base + jitter));
		};
		const poll = async () => {
			if (document.visibilityState === 'hidden' || !navigator.onLine) {
				schedule();
				return;
			}
			await fetchData(!initialFetchCompleteRef.current);
			if (stopped) return;
			schedule();
		};
		const refreshNow = () => {
			if (document.visibilityState === 'hidden' || !navigator.onLine) return;
			clearScheduledPoll();
			fetchData(!initialFetchCompleteRef.current).then(schedule);
		};
		const onOffline = () => setOffline(true);
		const onOnline = () => {
			setOffline(false);
			refreshNow();
		};
		const onVisibility = () => {
			if (document.visibilityState === 'visible') refreshNow();
		};
		schedule();
		window.addEventListener('focus', refreshNow);
		window.addEventListener('online', onOnline);
		window.addEventListener('offline', onOffline);
		document.addEventListener('visibilitychange', onVisibility);
		return () => {
			stopped = true;
			clearScheduledPoll();
			window.removeEventListener('focus', refreshNow);
			window.removeEventListener('online', onOnline);
			window.removeEventListener('offline', onOffline);
			document.removeEventListener('visibilitychange', onVisibility);
		};
	}, [authenticated, fetchData, hasExecutingActions]);

	const openRail = useCallback((requestFocus = false) => {
		isOpenRef.current = true;
		setIsOpen(true);
		setTransientPreviews((current) => current.filter((preview) => preview.kind !== 'ACTIVITY'));
		if (requestFocus) setFocusRequest((current) => current + 1);
	}, []);
	const closeRail = useCallback(() => {
		isOpenRef.current = false;
		setIsOpen(false);
	}, []);
	const toggleRail = useCallback(() => {
		setIsOpen((current) => {
			isOpenRef.current = !current;
			if (!current) {
				setTransientPreviews((previews) =>
					previews.filter((preview) => preview.kind !== 'ACTIVITY'),
				);
			}
			if (!current) setFocusRequest((value) => value + 1);
			return !current;
		});
	}, []);

	const setRailWidth = useCallback(
		(width: number) => {
			const next = clampWidth(width);
			setRailWidthState(next);
			if (typeof window !== 'undefined') window.localStorage.setItem(widthStorageKey, String(next));
		},
		[widthStorageKey],
	);
	const resetRailWidth = useCallback(() => setRailWidth(DEFAULT_RAIL_WIDTH), [setRailWidth]);

	const acknowledgeActivities = useCallback(
		async (notifications: ActivityNotification[], reason: ActivityAcknowledgeReason) => {
			const recipientIds = notifications
				.map((notification) => Number(notification.recipientId))
				.filter((recipientId) => Number.isInteger(recipientId) && recipientId > 0);
			if (!recipientIds.length) return;
			const recipientKeys = recipientIds.map(String);
			setActivityBusy((current) => ({
				...current,
				...recipientKeys.reduce<Record<string, boolean>>((next, key) => {
					next[key] = true;
					return next;
				}, {}),
			}));
			setActivityError('');
			try {
				await acknowledgeActivityNotifications(recipientIds, reason);
				const resolved = new Set(recipientKeys);
				setActivityNotifications((current) =>
					current.filter((notification) => !resolved.has(activityKey(notification))),
				);
			} catch (activityRequestError) {
				setActivityError(
					errorText(activityRequestError, 'The activity update could not be dismissed. Try again.'),
				);
			} finally {
				setActivityBusy((current) => {
					const next = { ...current };
					recipientKeys.forEach((key) => delete next[key]);
					return next;
				});
			}
		},
		[],
	);

	const viewActivityNotification = useCallback(
		(notification: ActivityNotification) => {
			const path = activityNotificationPath(notification);
			acknowledgeActivities([notification], 'OPENED');
			if (path) history.push(path);
		},
		[acknowledgeActivities, history],
	);

	const dismissActivityNotification = useCallback(
		(notification: ActivityNotification) => acknowledgeActivities([notification], 'DISMISSED'),
		[acknowledgeActivities],
	);

	const dismissAllActivityNotifications = useCallback(
		(notifications: ActivityNotification[]) => acknowledgeActivities(notifications, 'DISMISSED'),
		[acknowledgeActivities],
	);

	const replaceAction = useCallback((action: AutopilotAction) => {
		setActions((current) => mergeActions(current, [action]));
	}, []);

	const markActionSeen = useCallback(
		async (action: AutopilotAction) => {
			const key = actionKey(action.id);
			if (action.seenAt || seenActionIdsRef.current.has(key)) return;
			seenActionIdsRef.current.add(key);
			try {
				const updated = await markAutopilotActionSeen(action.id);
				replaceAction(updated);
			} catch (_seenError) {
				seenActionIdsRef.current.delete(key);
			}
		},
		[replaceAction],
	);

	const registerCardDraft = useCallback((actionId: number | string, dirty: boolean) => {
		const key = actionKey(actionId);
		setCardDraftIds((current) => {
			const contains = current.includes(key);
			if (dirty && !contains) return current.concat(key);
			if (!dirty && contains) return current.filter((item) => item !== key);
			return current;
		});
	}, []);

	const consumeActionFocus = useCallback((actionId: number | string) => {
		setFocusActionId((current) => (current === actionKey(actionId) ? null : current));
	}, []);
	const consumeActivityFocus = useCallback((recipientId: number | string) => {
		setFocusActivityId((current) => (current === String(recipientId) ? null : current));
	}, []);
	const dismissTransientPreview = useCallback((previewId: string) => {
		setTransientPreviews((current) => current.filter((preview) => preview.id !== previewId));
	}, []);
	const openTransientPreview = useCallback(
		(preview: AutopilotTransientPreview) => {
			const projectId =
				preview.kind === 'ACTION' ? preview.action.projectId : preview.notification.projectId;
			setQuestionScope(
				currentProjectId && Number(projectId) === currentProjectId ? 'PROJECT' : 'ORGANIZATION',
			);
			isOpenRef.current = true;
			setIsOpen(true);
			if (preview.kind === 'ACTION') {
				setFocusActionId(actionKey(preview.action.id));
				dismissTransientPreview(preview.id);
			} else {
				setFocusActivityId(activityKey(preview.notification));
				setTransientPreviews((current) =>
					current.filter((candidate) => candidate.kind !== 'ACTIVITY'),
				);
			}
		},
		[currentProjectId, dismissTransientPreview],
	);

	const runActionCommand = useCallback(
		async (
			action: AutopilotAction,
			command: ActionCommand,
			editedPreview?: AutopilotActionPreview,
		) => {
			const key = actionKey(action.id);
			setActionBusy((current) => ({ ...current, [key]: true }));
			setActionErrors((current) => ({ ...current, [key]: '' }));
			try {
				const payload = { version: action.version, editedPreview };
				const updated =
					command === 'approve'
						? await approveAutopilotAction(action.id, payload)
						: command === 'dismiss'
						? await dismissAutopilotAction(action.id, payload)
						: command === 'retry'
						? await retryAutopilotAction(action.id, payload)
						: await undoAutopilotAction(action.id, payload);
				knownActionVersionsRef.current.set(actionKey(updated.id), Number(updated.version || 0));
				knownActionStatusesRef.current.set(actionKey(updated.id), updated.status);
				replaceAction(updated);
				if (
					(command === 'approve' || command === 'retry' || command === 'undo') &&
					['EXECUTING', 'COMPLETED'].includes(updated.status)
				) {
					emitAffectedResourceRefresh(updated);
				}
				if (
					(command === 'approve' || command === 'retry') &&
					['APPROVED', 'ANALYZING', 'EXECUTING'].includes(updated.status)
				) {
					fetchData(false);
				}
				const refreshingSources =
					command === 'approve' && updated.status === 'ANALYZING' && !updated.decision;
				if ((command === 'approve' || command === 'dismiss') && !refreshingSources) {
					const pending = actions.filter(isPendingApproval);
					const currentIndex = pending.findIndex(
						(candidate) => actionKey(candidate.id) === actionKey(action.id),
					);
					const next = pending[currentIndex + 1] || pending[currentIndex - 1];
					setFocusActionId(next ? actionKey(next.id) : null);
				} else if (refreshingSources) {
					setFocusActionId(actionKey(updated.id));
				}
				setLiveAnnouncement(
					refreshingSources
						? `${action.title} is refreshing its sources and will require approval again.`
						: command === 'approve'
						? updated.status === 'COMPLETED'
							? `${action.title} completed and moved to Recent actions.`
							: ['APPROVED', 'ANALYZING', 'EXECUTING'].includes(updated.status)
							? `${action.title} started.`
							: `${action.title} approved.`
						: command === 'dismiss'
						? `${action.title} dismissed.`
						: command === 'retry'
						? `${action.title} queued to retry.`
						: `${action.title} was undone.`,
				);
				return true;
			} catch (commandError) {
				const message = autopilotActionErrorMessage(commandError);
				setActionErrors((current) => ({ ...current, [key]: message }));
				if (isVersionConflict(commandError)) await fetchData(false);
				return false;
			} finally {
				setActionBusy((current) => ({ ...current, [key]: false }));
			}
		},
		[actions, emitAffectedResourceRefresh, fetchData, replaceAction],
	);

	const commentOnAction = useCallback(
		async (action: AutopilotAction, comment: string) => {
			const value = comment.trim();
			if (!value) return false;
			const key = actionKey(action.id);
			setActionBusy((current) => ({ ...current, [key]: true }));
			setActionErrors((current) => ({ ...current, [key]: '' }));
			try {
				const updated = await commentOnAutopilotAction(action.id, action.version, value);
				replaceAction(updated);
				setLiveAnnouncement(`Comment added to ${action.title}.`);
				return true;
			} catch (commentError) {
				setActionErrors((current) => ({
					...current,
					[key]: errorText(commentError, 'Your comment could not be added. Try again.'),
				}));
				if (isVersionConflict(commentError)) await fetchData(false);
				return false;
			} finally {
				setActionBusy((current) => ({ ...current, [key]: false }));
			}
		},
		[fetchData, replaceAction],
	);

	const applyMode = useCallback(
		async (mode: AutomationMode, acknowledgement?: SelfDrivingAcknowledgement) => {
			if (!currentProjectId || !currentProjectSettings || modeBusy) return;
			if (mode === 'SELF_DRIVING' && !selfDrivingReleaseEnabled) {
				setModeError('Self-driving is not available yet. Choose Manual or Autopilot.');
				setSelfDrivingDialogOpen(false);
				return;
			}
			setModeBusy(true);
			setModeError('');
			try {
				const updated = await updateProjectAutomationMode(
					currentProjectId,
					mode,
					currentProjectSettings.version,
					acknowledgement,
				);
				setProjectSettings((current) => mergeProjectSettings(current, [updated]));
				setLiveAnnouncement(`Project mode changed to ${mode.replace('_', ' ').toLowerCase()}.`);
			} catch (modeRequestError) {
				setModeError(errorText(modeRequestError, 'The project mode could not be changed.'));
				if (isVersionConflict(modeRequestError)) await fetchData(false);
			} finally {
				setModeBusy(false);
			}
		},
		[currentProjectId, currentProjectSettings, fetchData, modeBusy, selfDrivingReleaseEnabled],
	);

	const requestModeChange = useCallback(
		async (mode: AutomationMode) => {
			if (!currentProjectSettings || mode === currentProjectSettings.mode) return;
			if (mode === 'SELF_DRIVING') {
				if (!selfDrivingReleaseEnabled) {
					setModeError('Self-driving is not available yet. Choose Manual or Autopilot.');
					return;
				}
				setSelfDrivingDialogOpen(true);
				return;
			}
			await applyMode(mode);
		},
		[applyMode, currentProjectSettings, selfDrivingReleaseEnabled],
	);

	const confirmSelfDriving = useCallback(async () => {
		if (!selfDrivingReleaseEnabled) {
			setSelfDrivingDialogOpen(false);
			setModeError('Self-driving is not available yet. Choose Manual or Autopilot.');
			return;
		}
		const acknowledgement: SelfDrivingAcknowledgement = {
			acknowledged: true,
			policyVersion: SELF_DRIVING_POLICY_VERSION,
			warningAcceptedAt: new Date().toISOString(),
		};
		await applyMode('SELF_DRIVING', acknowledgement);
		if (mountedRef.current) setSelfDrivingDialogOpen(false);
	}, [applyMode, selfDrivingReleaseEnabled]);

	const setOrganizationSelfDrivingAllowed = useCallback(
		async (allowed: boolean) => {
			if (!organizationSettings || organizationBusy) return;
			if (allowed && !selfDrivingReleaseEnabled) {
				setModeError('Self-driving is not available yet.');
				return;
			}
			setOrganizationBusy(true);
			setModeError('');
			try {
				const updated = await updateOrganizationSelfDriving(
					organizationSettings.organizationId,
					allowed,
					organizationSettings.version,
				);
				setOrganizationSettingsList((current) => mergeOrganizationSettings(current, [updated]));
				setLiveAnnouncement(
					allowed
						? 'Self-driving is available to this organization.'
						: 'Self-driving is disabled for this organization.',
				);
				await fetchData(false);
			} catch (organizationError) {
				setModeError(
					errorText(organizationError, 'The organization setting could not be changed.'),
				);
			} finally {
				setOrganizationBusy(false);
			}
		},
		[fetchData, organizationBusy, organizationSettings, selfDrivingReleaseEnabled],
	);

	const setDraft = useCallback(
		(value: string) => {
			setDrafts((current) => ({ ...current, [draftStorageKey]: value }));
			if (typeof window !== 'undefined') window.sessionStorage.setItem(draftStorageKey, value);
		},
		[draftStorageKey],
	);

	const sendQuestion = useCallback(
		async (overrideContent?: string) => {
			const content = String(overrideContent === undefined ? draft : overrideContent).trim();
			if (!content || questionBusy) return;
			if (questionScope === 'ORGANIZATION' && !conversationOrganizationId) {
				setLiveAnnouncement('Select an authorized organization before asking a question.');
				return;
			}
			const localId = `local-${Date.now()}`;
			const pendingAssistantId = `${localId}-assistant`;
			const optimistic: AutopilotMessage = {
				id: localId,
				conversationId: conversationId || 'pending',
				role: 'USER',
				content,
				createdAt: new Date().toISOString(),
				status: 'SENDING',
				projectId: currentProjectId,
			};
			const pendingAssistant: AutopilotMessage = {
				id: pendingAssistantId,
				conversationId: conversationId || 'pending',
				role: 'ASSISTANT',
				content: '',
				createdAt: new Date().toISOString(),
				status: 'STREAMING',
				projectId: currentProjectId,
				retryContent: content,
			};
			setMessages((current) => mergeMessages(current, [optimistic, pendingAssistant]));
			setDraft('');
			setQuestionBusy(true);
			const source = createAutopilotCancelSource();
			const requestContextKey = conversationContextKey;
			questionCancelSourceRef.current = source;
			activeQuestionMessageIdRef.current = localId;
			try {
				const response = conversationId
					? await sendAutopilotConversationMessage(
							conversationId,
							content,
							currentProjectId,
							questionScope,
							source.token,
							conversationOrganizationId,
					  )
					: await createAutopilotConversation(
							content,
							currentProjectId,
							questionScope,
							source.token,
							conversationOrganizationId,
					  );
				if (
					!mountedRef.current ||
					conversationContextRef.current !== requestContextKey ||
					questionCancelSourceRef.current !== source
				)
					return;
				setConversationId(response.conversationId);
				setMessages((current) =>
					mergeMessages(
						current.filter(
							(message) => message.id !== localId && message.id !== pendingAssistantId,
						),
						Array.isArray(response.messages) ? response.messages : [],
					),
				);
				if (response.action) replaceAction(response.action);
				setLiveAnnouncement('ScrubPlan answered your question.');
			} catch (questionError) {
				if (
					!mountedRef.current ||
					conversationContextRef.current !== requestContextKey ||
					questionCancelSourceRef.current !== source
				)
					return;
				const stopped = axios.isCancel(questionError);
				setMessages((current) =>
					current.map((message) =>
						message.id === localId
							? {
									...message,
									status: 'SENT',
							  }
							: message.id === pendingAssistantId
							? {
									...message,
									status: 'FAILED',
									errorMessage: stopped
										? 'Stopped waiting for this answer. You can try again.'
										: errorText(questionError, 'Your question could not be sent. Try again.'),
							  }
							: message,
					),
				);
			} finally {
				if (questionCancelSourceRef.current === source) {
					questionCancelSourceRef.current = null;
					activeQuestionMessageIdRef.current = null;
					setQuestionBusy(false);
				}
			}
		},
		[
			conversationId,
			conversationContextKey,
			conversationOrganizationId,
			currentProjectId,
			draft,
			questionBusy,
			questionScope,
			replaceAction,
			setDraft,
		],
	);

	const stopQuestion = useCallback(() => {
		const localId = activeQuestionMessageIdRef.current;
		const pendingAssistantId = localId ? `${localId}-assistant` : null;
		questionCancelSourceRef.current?.cancel('Question stopped by user');
		questionCancelSourceRef.current = null;
		activeQuestionMessageIdRef.current = null;
		setQuestionBusy(false);
		if (localId) {
			setMessages((current) =>
				current
					.filter((message) => message.id !== pendingAssistantId)
					.map((message) => (message.id === localId ? { ...message, status: 'SENT' } : message)),
			);
		}
		setLiveAnnouncement('Stopped waiting for the current answer.');
	}, []);

	const startNewChat = useCallback(async () => {
		if (questionBusy) return;
		if (questionScope === 'ORGANIZATION' && !conversationOrganizationId) {
			setLiveAnnouncement('Select an authorized organization before starting a chat.');
			return;
		}
		setQuestionBusy(true);
		const source = createAutopilotCancelSource();
		const requestContextKey = conversationContextKey;
		questionCancelSourceRef.current = source;
		try {
			const response = await createAutopilotConversation(
				'',
				currentProjectId,
				questionScope,
				source.token,
				conversationOrganizationId,
			);
			if (
				!mountedRef.current ||
				conversationContextRef.current !== requestContextKey ||
				questionCancelSourceRef.current !== source
			)
				return;
			setConversationId(response.conversationId);
			setMessages(Array.isArray(response.messages) ? response.messages : []);
			setLiveAnnouncement(
				draft.trim()
					? 'Started a new private chat. Your unsent draft was kept.'
					: 'Started a new private chat.',
			);
		} catch (newChatError) {
			if (
				conversationContextRef.current === requestContextKey &&
				questionCancelSourceRef.current === source &&
				!axios.isCancel(newChatError)
			) {
				setLiveAnnouncement('A new private chat could not be started. Try again.');
			}
		} finally {
			if (questionCancelSourceRef.current === source) {
				questionCancelSourceRef.current = null;
				setQuestionBusy(false);
			}
		}
	}, [
		conversationContextKey,
		conversationOrganizationId,
		currentProjectId,
		draft,
		questionBusy,
		questionScope,
	]);

	const pendingCount = useMemo(() => actions.filter(isPendingApproval).length, [actions]);
	const actionableCount = useMemo(() => actions.filter(requiresAutopilotAttention).length, [
		actions,
	]);

	const value = useMemo<AutopilotContextValue>(
		() => ({
			enabled,
			authenticated,
			isOpen,
			openRail,
			closeRail,
			toggleRail,
			focusRequest,
			railWidth,
			setRailWidth,
			resetRailWidth,
			loading,
			refreshing,
			offline,
			error,
			lastUpdatedAt,
			refresh: () => fetchData(!initialFetchCompleteRef.current),
			actions,
			messages,
			activityNotifications,
			activityBusy,
			activityError,
			viewActivityNotification,
			dismissActivityNotification,
			dismissAllActivityNotifications,
			pendingCount,
			actionableCount,
			actionBusy,
			actionErrors,
			runActionCommand,
			commentOnAction,
			currentProjectId,
			currentProjectSettings,
			organizationSettings,
			selfDrivingReleaseEnabled,
			authorizedOrganizations,
			selectedOrganizationId,
			setSelectedOrganizationId,
			modeBusy,
			modeError,
			requestModeChange,
			selfDrivingDialogOpen,
			confirmSelfDriving,
			cancelSelfDriving: () => setSelfDrivingDialogOpen(false),
			organizationBusy,
			setOrganizationSelfDrivingAllowed,
			draft,
			setDraft,
			questionScope,
			setQuestionScope,
			questionBusy,
			sendQuestion,
			stopQuestion,
			startNewChat,
			markActionSeen,
			registerCardDraft,
			hasUnsavedCardDrafts: cardDraftIds.length > 0,
			focusActionId,
			consumeActionFocus,
			liveAnnouncement,
			transientPreviews,
			dismissTransientPreview,
			openTransientPreview,
			focusActivityId,
			consumeActivityFocus,
		}),
		[
			actionableCount,
			actionBusy,
			actionErrors,
			actions,
			activityBusy,
			activityError,
			activityNotifications,
			authorizedOrganizations,
			authenticated,
			closeRail,
			commentOnAction,
			confirmSelfDriving,
			consumeActivityFocus,
			consumeActionFocus,
			currentProjectId,
			currentProjectSettings,
			draft,
			dismissActivityNotification,
			dismissAllActivityNotifications,
			dismissTransientPreview,
			enabled,
			error,
			fetchData,
			focusRequest,
			focusActivityId,
			focusActionId,
			isOpen,
			lastUpdatedAt,
			liveAnnouncement,
			loading,
			messages,
			modeBusy,
			modeError,
			offline,
			openRail,
			openTransientPreview,
			organizationBusy,
			organizationSettings,
			selfDrivingReleaseEnabled,
			cardDraftIds,
			pendingCount,
			questionBusy,
			questionScope,
			selectedOrganizationId,
			railWidth,
			refreshing,
			requestModeChange,
			resetRailWidth,
			runActionCommand,
			sendQuestion,
			startNewChat,
			stopQuestion,
			markActionSeen,
			registerCardDraft,
			setDraft,
			setSelectedOrganizationId,
			setOrganizationSelfDrivingAllowed,
			setRailWidth,
			selfDrivingDialogOpen,
			toggleRail,
			transientPreviews,
			viewActivityNotification,
		],
	);

	return <AutopilotContext.Provider value={value}>{children}</AutopilotContext.Provider>;
};

export default AutopilotProvider;
